import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, X, MapPin, Phone, Calendar, Upload, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import ImageUploadSimple from '@/components/ImageUploadSimple';
import BottomNavigation from '@/components/BottomNavigation';
import { useIsMobile } from "@/hooks/use-mobile";
import AddServiceSheet from "@/components/AddServiceSheet";

interface ImageFile {
  url: string;
  name: string;
  size: number;
  type: string;
  file?: File;
}

export const AddServiceForm: React.FC<{
  formData: any;
  setFormData: React.Dispatch<any>;
  isLoading: boolean;
  handleInputChange: any;
  handleImagesChange: any;
  handleSubmit: any;
  navigate: any;
}> = ({ formData, setFormData, isLoading, handleInputChange, handleImagesChange, handleSubmit, navigate }) => (
  <form onSubmit={handleSubmit} className="space-y-6">
    {/* Basic Info Card */}
    <Card className="card-premium">
      <CardHeader>
        <CardTitle className="text-lg font-serif flex items-center gap-2 text-navy-800">
          <Plus className="h-5 w-5 text-navy-600" />
          Basis Informatie
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="name" className="text-navy-700">Dienst Naam *</Label>
          <Input 
            id="name" 
            name="name" 
            value={formData.name}
            onChange={handleInputChange}
            required
            placeholder="Bijv. Yoga les"
            className="mt-1 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
          />
        </div>

        <div>
          <Label htmlFor="description" className="text-navy-700">Beschrijving</Label>
          <Textarea 
            id="description" 
            name="description" 
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            placeholder="Beschrijf je dienst in detail..."
            className="mt-1 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
          />
        </div>

        <div>
          <Label htmlFor="category" className="text-navy-700">Categorie *</Label>
          <Select 
            onValueChange={(value) => setFormData((prev) => ({ ...prev, category: value }))}
            value={formData.category}
          >
            <SelectTrigger id="category" className="mt-1 border-cream-300 bg-white">
              <SelectValue placeholder="Kies een categorie" />
            </SelectTrigger>
            <SelectContent className="bg-white border-cream-300">
              {( ([
                { value: "yoga", label: "🧘‍♀️ Yoga" },
                { value: "coaching", label: "💪 Coaching" },
                { value: "workshop", label: "🎓 Workshop" },
                { value: "consulting", label: "💼 Consulting" },
                { value: "massage", label: "💆‍♀️ Massage" },
                { value: "training", label: "🏋️‍♂️ Training" },
              ]) ?? []).map(({value, label}) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Price and Duration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="price" className="text-navy-700">Prijs (€) *</Label>
            <Input 
              id="price" 
              name="price" 
              type="number"
              min="0"
              step="0.01"
              value={formData.price}
              onChange={handleInputChange}
              required
              placeholder="50.00"
              className="mt-1 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
            />
          </div>
          <div>
            <Label htmlFor="duration" className="text-navy-700">Duur (minuten)</Label>
            <Input 
              id="duration" 
              name="duration" 
              type="number"
              min="1"
              value={formData.duration}
              onChange={handleInputChange}
              placeholder="60"
              className="mt-1 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="location" className="text-navy-700">Locatie</Label>
          <Input 
            id="location" 
            name="location" 
            value={formData.location}
            onChange={handleInputChange}
            placeholder="Bijv. Amsterdam, of bij klant thuis"
            className="mt-1 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
          />
        </div>
      </CardContent>
    </Card>

    {/* Images Card */}
    <Card className="card-premium">
      <CardHeader>
        <CardTitle className="text-lg font-serif text-navy-800">Foto's & Media</CardTitle>
        <p className="text-sm text-navy-600">
          Upload foto's van je dienst om meer klanten aan te trekken
        </p>
      </CardHeader>
      <CardContent>
        <ImageUploadSimple 
          images={(formData.images ?? []).filter(img => img && typeof img === 'object' && img.file)} 
          onImagesChange={handleImagesChange} 
          multiple={true} 
        />
        {/* WOM info-banner stijl tips */}
        <div className="mt-6">
          <div className="bg-navy-50/80 border border-navy-200 rounded-2xl px-5 py-4 flex gap-4 items-start shadow-lg backdrop-blur-sm">
            <svg viewBox="0 0 24 24" width="26" height="26" className="text-navy-400 flex-shrink-0 mt-1"><circle cx="12" cy="12" r="10" fill="#F0F6FF"/><path d="M12 8v4" stroke="#274472" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="16" r="1" fill="#274472"/></svg>
            <div>
              <p className="font-semibold text-navy-900 mb-2 tracking-tight">Tips voor goede foto's:</p>
              <ul className="list-disc ml-4 space-y-1 text-base text-navy-800">
                <li>Toon je werk, proces of eindresultaat</li>
                <li>Gebruik goede verlichting</li>
                <li>Varieer met verschillende hoeken</li>
                <li>Professionele uitstraling werkt het beste</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Submit Buttons */}
    <div className="flex gap-3 pt-4 pb-8">
      <Button
        type="button"
        variant="outline"
        onClick={() => navigate(-1)}
        className="flex-1 btn-elegant-secondary"
      >
        Annuleren
      </Button>
      <Button 
        type="submit" 
        className="flex-1 btn-elegant-primary"
        disabled={isLoading}
      >
        {isLoading ? 'Opslaan...' : 'Dienst Publiceren'}
      </Button>
    </div>
  </form>
);

function AddService({ initialData }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(initialData || {
    name: '',
    description: '',
    category: '',
    price: '',
    duration: '',
    location: '',
    images: [] as ImageFile[],
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);
  const isMobile = useIsMobile();

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function handleImagesChange(images: ImageFile[]) {
    setFormData((prev) => ({ ...prev, images }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);

    if (!formData.name.trim() || !formData.category.trim() || !formData.price.trim()) {
      toast.error('Vul a.u.b. de verplichte velden in: naam, categorie en prijs.');
      setIsLoading(false);
      return;
    }

    if (!user) {
      toast.error('Je moet ingelogd zijn om een dienst te plaatsen.');
      setIsLoading(false);
      return;
    }

    const priceNum = Number(formData.price);
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error('Prijs moet een geldig positief getal zijn.');
      setIsLoading(false);
      return;
    }

    try {
      // Insert service into database
      const { data, error } = await supabase.from('services').insert([{ 
        name: formData.name,
        description: formData.description,
        category: formData.category,
        price: priceNum,
        duration: formData.duration,
        location: formData.location,
        user_id: user.id,
        images: formData.images,
        created_at: new Date().toISOString(),
      }]);

      if (error) {
        console.error('Failed to add service:', error);
        toast.error('Er is een fout opgetreden bij het opslaan van de dienst. Probeer het later opnieuw.');
        return;
      }

      toast.success(`🎉 Dienst "${formData.name}" succesvol geplaatst!`);
      navigate('/profiel?tab=services');
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error('Er is een onverwachte fout opgetreden.');
    } finally {
      setIsLoading(false);
    }
  }

  if (isMobile) {
    // Mobiele Sheet
    return (
      <AddServiceSheet open={true} onClose={() => navigate(-1)}>
        <div className="flex flex-col flex-1 min-h-0 h-full">
          <div className="flex-1 overflow-y-auto min-h-0 pb-24">
            <AddServiceForm
              formData={formData}
              setFormData={setFormData}
              isLoading={isLoading}
              handleInputChange={handleInputChange}
              handleImagesChange={handleImagesChange}
              handleSubmit={handleSubmit}
              navigate={navigate}
            />
          </div>
          <BottomNavigation />
        </div>
      </AddServiceSheet>
    );
  }

  // Bestaande desktop UI render
  return (
    <div className="min-h-screen bg-elegant pb-32">
      {/* Header */}
      <div className="glass-effect border-b border-cream-300">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="hover:bg-cream-100 text-navy-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-serif text-navy-800">Dienst Plaatsen</h1>
              <p className="text-sm text-navy-600">Deel je expertise met de WOM community</p>
            </div>
          </div>

          {/* Service Header */}
          <div className="card-premium p-4 mb-4">
            <div className="text-center">
              <div className="mb-2">
                <h1 className="script-text text-3xl text-navy-800">Nieuwe Dienst</h1>
              </div>
              <p className="text-sm text-navy-600">
                Deel je talent met vrouwen die jouw expertise nodig hebben
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-6 py-8 pb-16">
        <AddServiceForm
          formData={formData}
          setFormData={setFormData}
          isLoading={isLoading}
          handleInputChange={handleInputChange}
          handleImagesChange={handleImagesChange}
          handleSubmit={handleSubmit}
          navigate={navigate}
        />
      </div>

      <BottomNavigation />
    </div>
  );
}

export default AddService;